import { ArrowUpRight } from "lucide-react";
import { HERO_IMAGE } from "@/mock";
import { useLanguage } from "@/i18n/LanguageContext";

const SportsMarquee = () => {
  const { t } = useLanguage();
  const row = [...t.sports, ...t.sports];
  return (
    <div data-testid="sports-strip" className="relative bg-[#F4F4F2] border-y border-neutral-200 py-5 overflow-hidden marquee-pause">
      <div className="flex w-max animate-marquee whitespace-nowrap">
        {row.map((s, i) => (
          <div key={i} className="flex items-center" data-testid={i < t.sports.length ? `sports-strip-item-${i}` : undefined}>
            <span className={`font-display text-2xl md:text-3xl px-6 ${i % 2 === 0 ? "text-[#2E4FE6]" : "text-[#111]"}`}>{s.toUpperCase()}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#F2641E]" />
          </div>
        ))}
      </div>
    </div>
  );
};

const Hero = () => {
  const { t } = useLanguage();
  return (
    <section id="top" data-testid="hero-section" className="relative pt-[72px] bg-[#F4F4F2]">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-8 py-8 md:py-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="reveal">
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="w-8 h-[2px] bg-[#F2641E]" />
              <span className="text-[#2E4FE6] font-bold text-xs tracking-[0.22em] uppercase">{t.hero.eyebrow}</span>
            </div>
            <h1 data-testid="hero-headline" className="font-display text-[clamp(2.6rem,6vw,5rem)] uppercase break-words">
              <span className="text-[#111]">{t.hero.title1}</span>
              <span className="text-[#2E4FE6]">{t.hero.title2}</span>
              <span className="text-[#F2641E]">.</span>
            </h1>
            <p data-testid="hero-subtitle" className="mt-7 text-lg text-neutral-600 max-w-md mx-auto leading-relaxed">
              {t.hero.subtitle.split(t.hero.boldWord).map((part, i, arr) => (
                <span key={i}>
                  {part}
                  {i < arr.length - 1 && <strong className="font-bold text-[#111]">{t.hero.boldWord}</strong>}
                </span>
              ))}
            </p>
            <div
              data-testid="hero-development-badge"
              className="mt-7 inline-flex items-center gap-2 bg-[#2E4FE6]/10 border border-[#2E4FE6]/20 rounded-full px-3.5 py-1.5"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F2641E] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F2641E]" />
              </span>
              <span className="text-[#2E4FE6] font-semibold text-xs tracking-wide">{t.hero.devNote}</span>
            </div>
            <div>
              <a
                href="#contact"
                data-testid="hero-get-in-touch-button"
                className="group mt-7 inline-flex items-center gap-2 bg-[#F2641E] hover:bg-[#dd560f] text-white font-bold text-sm uppercase tracking-wide px-7 py-4 rounded-md transition-colors duration-200"
              >
                {t.hero.cta}
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>
          </div>
          <div className="reveal relative">
            <div className="absolute -top-3 -left-3 w-20 h-20 border-t-4 border-l-4 border-[#F2641E] rounded-tl-xl pointer-events-none z-10" />
            <div className="absolute -bottom-3 -right-3 w-20 h-20 border-b-4 border-r-4 border-[#2E4FE6] rounded-br-xl pointer-events-none z-10" />
            <div className="relative aspect-square rounded-xl overflow-hidden shadow-2xl ring-1 ring-black/5">
              <img src={HERO_IMAGE} alt={t.hero.imageAlt} className="w-full h-full object-cover animate-kenburns" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10" />
              <div className="absolute bottom-6 left-6 flex items-center gap-2.5">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F2641E] opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-[#F2641E]" />
                </span>
                <span className="text-white font-display text-2xl uppercase tracking-wide">{t.hero.badge}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SportsMarquee />
    </section>
  );
};

export default Hero;
